// useUpdateCheck — shared update-check logic for both the auto-banner
// (fires once on mount) and the manual "Check for updates" button in
// settings. Compares the wrapper's installed versionName against the
// server's latest tagged-release version via semver-gt.
//
// Returns the latest version + a `manualCheck()` function. The hook
// auto-runs the check on mount; settings can call manualCheck() to
// retry on demand (also clears any prior dismissal so a freshly-
// discovered update reappears in the banner).

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, type AppLatestAndroid } from "./rpc";

interface VultureUpdatesBridge {
  versionInfo(): string;
  install(url: string): string;
  // v0.3.4+ — diagnostics surface used by Settings → Push status
  // page. Older wrappers don't expose these; feature-detect.
  diagSnapshot?(): string;
  rePushRegister?(): string;
  pushStateInfo?(): string;
}
interface BridgeVersion {
  versionName?: string;
  versionCode?: number;
  gitSha?: string;
}

declare global {
  interface Window {
    VultureUpdates?: VultureUpdatesBridge;
  }
}

export interface UpdateState {
  /** True when the server has a tagged release strictly newer than
   *  the installed versionName. False when up to date OR when there
   *  is no wrapper bridge (browser PWA). */
  available: boolean;
  /** Server-side latest version string (e.g. "0.2.5"), if known. */
  latestVersion: string | null;
  /** Installed versionName from the wrapper bridge. */
  installedVersion: string | null;
  /** Download URL (relative path, e.g. "/download/...") of the new APK. */
  downloadUrl: string | null;
  /** True while a check is in flight. */
  checking: boolean;
  /** "in_wrapper": modern Android wrapper with VultureUpdates bridge —
   *  one-tap install via bridge.install(). "old_wrapper": Android WebView
   *  detected (UA contains "wv") but no bridge — pre-v0.2.5 APK that
   *  shipped before UpdatesBridge existed. Offer manual download.
   *  "browser": plain web, SW handles bundle updates. */
  surface: "in_wrapper" | "old_wrapper" | "browser";
}

/**
 * Semver-gt — returns true iff `a` is strictly greater than `b`.
 * Pre-release suffixes (e.g. "0.2.5-rc1") are compared lexicographically
 * AFTER the numeric part matches, which means "0.2.5" > "0.2.5-rc1"
 * (no suffix beats any suffix, matching common pre-release semantics).
 */
function semverGt(a: string, b: string): boolean {
  const parse = (s: string) => {
    const [core, pre] = s.split("-", 2);
    const nums = core.split(".").map((n) => Number.parseInt(n, 10) || 0);
    return { nums: [nums[0] || 0, nums[1] || 0, nums[2] || 0], pre: pre || "" };
  };
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < 3; i++) {
    if (pa.nums[i] > pb.nums[i]) return true;
    if (pa.nums[i] < pb.nums[i]) return false;
  }
  // Core equal — empty pre > any pre, otherwise lexicographic.
  if (pa.pre === pb.pre) return false;
  if (pa.pre === "") return true;
  if (pb.pre === "") return false;
  return pa.pre > pb.pre;
}

async function fetchLatest(): Promise<AppLatestAndroid | null> {
  try { return await api.appLatestAndroid(); }
  catch { return null; }
}

function readInstalled(): string | null {
  const bridge = window.VultureUpdates;
  if (!bridge) return null;
  try {
    const info: BridgeVersion = JSON.parse(bridge.versionInfo() || "{}");
    return info.versionName || null;
  } catch { return null; }
}

/** Pick the surface we're rendering for. The bridge is present iff the
 *  wrapper is recent enough (v0.2.5+) to have UpdatesBridge.kt; older
 *  APKs (≤ v0.2.4) are still WebViews but lack the JS bridge entirely.
 *  Treating those as "browser" misleads the user — the page can't
 *  hard-refresh into a new APK. Detect them via the WebView UA token
 *  ("; wv)" / " wv "), distinct from regular Chrome on Android. */
function detectSurface(): "in_wrapper" | "old_wrapper" | "browser" {
  if (typeof window === "undefined") return "browser";
  if (window.VultureUpdates) return "in_wrapper";
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  // The Android WebView identifier is the literal token "wv" inside the
  // Chrome UA string. Regular Chrome on Android does not include it.
  if (/\bAndroid\b/.test(ua) && /; wv\)|\bwv\b/.test(ua)) return "old_wrapper";
  return "browser";
}

export function useUpdateCheck(): UpdateState & {
  manualCheck: () => Promise<UpdateState>;
  install: () => "ok" | "no_bridge" | "no_update" | string;
} {
  const surface = detectSurface();
  const inWrapper = surface === "in_wrapper";
  const [state, setState] = useState<UpdateState>({
    available: false,
    latestVersion: null,
    installedVersion: readInstalled(),
    downloadUrl: null,
    checking: false,
    surface,
  });

  const runCheck = useCallback(async (): Promise<UpdateState> => {
    const installed = readInstalled();
    setState((prev) => ({ ...prev, checking: true, installedVersion: installed }));
    const latest = await fetchLatest();
    const next: UpdateState = {
      available: false,
      latestVersion: latest?.version || null,
      installedVersion: installed,
      downloadUrl: latest?.downloadUrl || null,
      checking: false,
      surface,
    };
    // Banner only fires in the wrapper. Browser users still see the
    // check result via settings (so they know what's the latest tag)
    // but `available: false` there since there's nothing to install.
    if (inWrapper && latest?.available && latest.version && installed) {
      // "dev" build = never auto-update against a CI tag.
      if (installed.toLowerCase() !== "dev" && semverGt(latest.version, installed)) {
        next.available = true;
      }
    }
    setState(next);
    return next;
  }, [inWrapper, surface]);

  // Auto-run once on mount.
  useEffect(() => {
    let cancelled = false;
    runCheck().catch(() => { /* state already reset by runCheck */ });
    return () => { cancelled = true; void cancelled; };
  }, [runCheck]);

  const install = useCallback((): "ok" | "no_bridge" | "no_update" | string => {
    const bridge = window.VultureUpdates;
    if (!bridge) return "no_bridge";
    if (!state.available || !state.downloadUrl) return "no_update";
    return bridge.install(state.downloadUrl);
  }, [state.available, state.downloadUrl]);

  return { ...state, manualCheck: runCheck, install };
}
